import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import {
  useActiveAccountId,
  useFeatures,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import LexicalReadOnlyMarkdownViewer from '@lifeomic/phc-web-toolkit/src/components/MarkdownEditor/Lexical/LexicalReadOnlyMarkdownViewer';
import { memberDetailMessages } from './messages';
import { useDataset } from '../../../../util/api';
import {
  getDetectedIssueCategoryDisplay,
  getDetectedIssueCitations,
  getDetectedIssueExplanationMarkdown,
  getDetectedIssueReviewStatus,
  isActiveDetectedIssue,
  isPendingDetectedIssue,
} from './detectedIssueUtils';

const useStyles = makeStyles(theme => ({
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  modalSection: {
    marginBottom: theme.spacing(2),
  },
}));

const detectedIssueFhirService = new FhirService('DetectedIssue');

type DetectedIssuesRow = {
  id: string;
  resource: fhir.DetectedIssue;
  category: string;
  severity: string;
  reviewStatus: string;
  detail: string;
  date: string;
};

const toRow = (resource: fhir.DetectedIssue): DetectedIssuesRow => ({
  id: resource.id || '',
  resource,
  category: getDetectedIssueCategoryDisplay(resource),
  severity: resource.severity || '—',
  reviewStatus: getDetectedIssueReviewStatus(resource) || '—',
  detail: resource.detail || '—',
  date: resource.date || '—',
});

export type DetectedIssuesContentProps = {
  memberId: string;
  fallback: React.ReactNode;
};

export const DetectedIssuesContent: React.FC<DetectedIssuesContentProps> = ({
  memberId,
  fallback,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const hasFeature = useFeatures();
  const accountId = useActiveAccountId();
  const serviceAuth = useServiceAuth();
  const dataset = useDataset();
  const [fetchIssues, fetchState] = useService(detectedIssueFhirService.get);
  const [activeIssues, setActiveIssues] = useState<fhir.DetectedIssue[]>([]);
  const [pendingIssues, setPendingIssues] = useState<fhir.DetectedIssue[]>([]);
  const [selectedIssue, setSelectedIssue] = useState<fhir.DetectedIssue | null>(
    null,
  );

  const showReviewQueue = hasFeature('detectedIssuesReviewQueue');

  const loadIssues = useCallback(async () => {
    if (!accountId || !memberId || !serviceAuth) return;
    try {
      const responseWithParams = await fetchIssues(accountId, {
        patient: memberId,
        _tag: [dataset.codingToken],
        _count: 200,
      });
      const entry = responseWithParams?.data?.entry ?? [];
      const resources = entry
        .map((e: { resource?: fhir.DetectedIssue }) => e.resource)
        .filter(Boolean) as fhir.DetectedIssue[];
      setActiveIssues(resources.filter(isActiveDetectedIssue));
      setPendingIssues(
        showReviewQueue ? resources.filter(isPendingDetectedIssue) : [],
      );
    } catch {
      setActiveIssues([]);
      setPendingIssues([]);
    }
  }, [
    accountId,
    memberId,
    serviceAuth,
    fetchIssues,
    dataset.codingToken,
    showReviewQueue,
  ]);

  useEffect(() => {
    void loadIssues();
  }, [loadIssues]);

  if (!hasFeature('detectedIssuesUi')) {
    return <>{fallback}</>;
  }

  const buildColumns = () => [
    {
      header: {
        label: formatMessage(memberDetailMessages.detectedIssuesCategory),
      },
      cell: { content: (row: DetectedIssuesRow) => row.category },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.detectedIssuesSeverity),
      },
      cell: { content: (row: DetectedIssuesRow) => row.severity },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.detectedIssuesReviewStatus),
      },
      cell: { content: (row: DetectedIssuesRow) => row.reviewStatus },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.detectedIssuesDetail),
      },
      cell: { content: (row: DetectedIssuesRow) => row.detail },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.detectedIssuesDate),
      },
      cell: { content: (row: DetectedIssuesRow) => row.date },
    },
  ];

  const renderTable = (
    issues: fhir.DetectedIssue[],
    emptyMessage: { id: string; defaultMessage: string },
  ) => (
    <TableModule
      isLoading={fetchState.isFetching}
      config={buildColumns()}
      data={issues.map(toRow)}
      noResultsMessage={formatMessage(emptyMessage)}
      onRowClick={(row: DetectedIssuesRow) => setSelectedIssue(row.resource)}
      rowClickLabel={formatMessage(pwtSharedMessages.viewDetails)}
      rowRole="link"
    />
  );

  if (fetchState.isFetching && activeIssues.length === 0) {
    return <>{fallback}</>;
  }

  const explanation = selectedIssue
    ? getDetectedIssueExplanationMarkdown(selectedIssue)
    : undefined;
  const citations = selectedIssue
    ? getDetectedIssueCitations(selectedIssue)
    : [];

  return (
    <div className={classes.tabPanel} data-testid="detected-issues-panel">
      <Text weight="bold" className={classes.sectionTitle}>
        {formatMessage(memberDetailMessages.detectedIssuesActiveTitle)}
      </Text>
      {renderTable(activeIssues, memberDetailMessages.detectedIssuesEmpty)}
      {showReviewQueue ? (
        <>
          <Text weight="bold" className={classes.sectionTitle}>
            {formatMessage(memberDetailMessages.detectedIssuesReviewQueueTitle)}
          </Text>
          {renderTable(
            pendingIssues,
            memberDetailMessages.detectedIssuesReviewQueueEmpty,
          )}
        </>
      ) : null}
      {selectedIssue ? (
        <Modal
          isOpen
          onDismiss={() => setSelectedIssue(null)}
          title={formatMessage(memberDetailMessages.detectedIssuesDetailTitle)}
          data-testid="detected-issue-view-modal"
        >
          <Box padding={2} direction="column">
            <div className={classes.modalSection}>
              <Text size="body">{selectedIssue.detail}</Text>
            </div>
            {explanation ? (
              <div className={classes.modalSection}>
                <LexicalReadOnlyMarkdownViewer
                  markdown={explanation}
                  namespace="detected-issues-explanation"
                  syncKey={
                    selectedIssue.id ?? selectedIssue.detail ?? 'detected-issue'
                  }
                />
              </div>
            ) : null}
            {citations.length > 0 ? (
              <div className={classes.modalSection}>
                <Text weight="bold">
                  {formatMessage(memberDetailMessages.detectedIssuesCitations)}
                </Text>
                {citations.map(citation => (
                  <Text key={citation.reference} size="body">
                    {citation.display} ({citation.reference})
                  </Text>
                ))}
              </div>
            ) : null}
          </Box>
        </Modal>
      ) : null}
    </div>
  );
};
